import { useState, useEffect, useMemo } from 'react';
import type { ZprToggleCustomEvent } from '@zappar/zpr-ui-components-stencil';
import { IAsset } from '../providers';
import { useAssetActions } from '../context/AssetActionsContext';
import { useEchoContext } from '../echo/EchoContext';
import { publicLinkCreatedBody } from '../echo/systemMessages';
import { Client, IPublicLink } from '@zappar/zw-api-client';
import { ZprToggle } from '@zpr-stencil';
import { formatDate, slugify } from '../utils';
import { FailIcon, SuccessIcon, ProcessingIcon } from '../../../icons';
import { MediaType } from '../types';
import { InlineInput } from '../..';

type SizeOption = {
  id: string;
  label: string;
  value: string | boolean;
};

type EmbedQuerystring = {
  experience?: string;
  width?: string;
  height?: string;
  pan?: boolean;
  zoom?: boolean;
  rotateHorizontal?: boolean;
  rotateVertical?: boolean;
  scale?: boolean;
};

type EmbedTemplate = {
  label: string;
  value: string;
  mediaTypes?: MediaType[];
  buildUrl: (backendUrl: string, queryParams?: EmbedQuerystring) => string;
  embedOptions: SizeOption[];
};

function extractFolder(url: string): string {
  const segments = new URL(url).pathname.split('/').filter(Boolean);
  return segments.slice(0, -1).join('/');
}

function generateQueryStringFromObject(obj: Record<string, string | boolean>): string {
  const params = new URLSearchParams();
  for (const key in obj) {
    params.append(key, String(obj[key]));
  }
  console.log(params.toString());
  return params.toString();
}

export const EMBED_TEMPLATES: EmbedTemplate[] = [
  {
    label: 'File only',
    value: 'file',
    mediaTypes: Object.values(MediaType).filter(t => t !== MediaType.Model3D),
    buildUrl: (url) => url,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
    ],
  },
  {
    label: '3D Viewer',
    value: 'basic-3d-viewer',
    mediaTypes: [MediaType.Model3D],
    buildUrl: (url, queryParams) => `https://7lgrv.zappar-us.io/viewer/?key=${extractFolder(url)}&${generateQueryStringFromObject(queryParams ?? {})}`,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
      { id: 'pan', label: 'Pan', value: true },
      { id: 'zoom', label: 'Zoom', value: true },
      { id: 'rotateHorizontal', label: 'Rotate (horizontal)', value: true },
      { id: 'rotateVertical', label: 'Rotate (vertical)', value: true },
      { id: 'scale', label: 'Scale', value: true },
    ],
  },
  {
    label: 'Nestle – 3D Viewer',
    value: 'nestle-3d-viewer',
    mediaTypes: [MediaType.Model3D],
    buildUrl: (url, queryParams) => `https://nest-mv.zap.works/?key=${extractFolder(url)}&${generateQueryStringFromObject(queryParams ?? {})}`,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
      { id: 'pan', label: 'Pan', value: true },
      { id: 'zoom', label: 'Zoom', value: true },
      { id: 'rotateHorizontal', label: 'Rotate (horizontal)', value: true },
      { id: 'rotateVertical', label: 'Rotate (vertical)', value: true },
      { id: 'scale', label: 'Scale', value: true },
    ],
  },
];

function buildSizeOptionsFromQuerystring(qs: EmbedQuerystring, defaults: SizeOption[]): SizeOption[] {
  return defaults.map(opt => {
    const key = opt.id as keyof EmbedQuerystring;
    if (key in qs) return { ...opt, value: qs[key] as string | boolean };
    return opt;
  });
}

function buildQuerystringFromForm(experience: string, sizeOptions: SizeOption[]): EmbedQuerystring {
  const qs: EmbedQuerystring = { experience };
  for (const opt of sizeOptions) {
    (qs as Record<string, unknown>)[opt.id] = opt.value;
  }
  return qs;
}

function buildEmbedCode(url: string, sizeOptions: SizeOption[]): string {
  const width = sizeOptions.find(o => o.id === 'width')?.value ?? '640';
  const height = sizeOptions.find(o => o.id === 'height')?.value ?? '480';
  const embedCode =  `<iframe src="${url}" sandbox="allow-scripts allow-same-origin" width="${width}" height="${height}" frameborder="0" loading="lazy" allow="camera; gyroscope; accelerometer; magnetometer; xr-spatial-tracking;"></iframe>`;
  return embedCode;
}

// Append a one-off cache-buster so the *preview* re-fetches the freshly published
// bytes from the CDN. The canonical Direct link / Embed code stay clean.
function bustUrl(url: string, token: number): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_=${token}`;
}

// After republish, poll the public CDN until it serves the new bytes before
// refreshing the preview. Invalidations usually land within seconds.
const CDN_POLL_INTERVAL_MS = 2500;
const CDN_POLL_TIMEOUT_MS = 90000;

// HEAD the public asset and read its `Last-Modified` (public-media allows CORS;
// `Last-Modified` is a CORS-safelisted response header). Returns null on failure.
async function headLastModified(url: string): Promise<string | null> {
  try {
    const resp = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    if (!resp.ok) return null;
    return resp.headers.get('Last-Modified');
  } catch {
    return null;
  }
}

// Poll the CDN edge until it stops serving `baseline` — i.e. the republished
// bytes have propagated — or until the timeout elapses.
async function waitForCdnRefresh(url: string, baseline: string | null): Promise<void> {
  const deadline = Date.now() + CDN_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const current = await headLastModified(url);
    if (current && current !== baseline) return;
    await new Promise(resolve => setTimeout(resolve, CDN_POLL_INTERVAL_MS));
  }
}

const PUBLIC_LINK_ERROR_MESSAGES: Record<string, string> = {
  PublicLinkAlreadyExistsErrorResponse: 'A public link already exists for this file.',
  PublicLinkFolderConflictErrorResponse: 'A public link with that slug already exists. Please choose a different slug.',
  InvalidRequestBodyResponse: 'Invalid request. Please check your input and try again.',
  version_not_found: 'A newer version has been uploaded since you opened this tab. Please refresh.',
  version_not_ready: 'This version is still being processed. Please wait until it has finished and try again.',
};

function getPublicLinkErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    // Conventional APIError-shaped error (has `code`)
    const code = 'code' in err ? (err as { code: unknown }).code : undefined;
    if (typeof code === 'string' && code in PUBLIC_LINK_ERROR_MESSAGES) {
      return PUBLIC_LINK_ERROR_MESSAGES[code];
    }
    // The publish endpoint returns `{ error: 'version_not_found', currentVersion: N }`
    // (or `version_not_ready`) rather than the standard `{ code, message }` envelope.
    const error = 'error' in err ? (err as { error: unknown }).error : undefined;
    if (typeof error === 'string' && error in PUBLIC_LINK_ERROR_MESSAGES) {
      return PUBLIC_LINK_ERROR_MESSAGES[error];
    }
  }
  return fallback;
}

export const PublicLinkTab = ({ asset, zwApiClient, onEmbedCodeBuilt, onPublicLinkCreated, onPublished, onError, havePermissionToPublish }: { asset: IAsset; zwApiClient: Client; onEmbedCodeBuilt?: (embedCode: string) => void; onPublicLinkCreated?: () => void; onPublished?: () => void; onError?: (message: string) => void; havePermissionToPublish?: boolean }) => {
  const { workspaceId } = useAssetActions();
  const { postSystemComment } = useEchoContext();
  const assetId = asset.getId();

  const availableTemplates = useMemo(() => {
    const mediaType = asset.getMediaType();
    return EMBED_TEMPLATES.filter(t => !t.mediaTypes || t.mediaTypes.includes(mediaType));
  }, [asset]);

  // undefined = loading, null = no link exists, IPublicLink = link exists
  const [publicLink, setPublicLink] = useState<IPublicLink | null | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRepublishing, setIsRepublishing] = useState(false);
  const [previewBust, setPreviewBust] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmbedTemplate>(availableTemplates[0]);
  const [sizeConfig, setSizeConfig] = useState<SizeOption[]>(availableTemplates.length > 0 ? availableTemplates[0].embedOptions : []);
  const [customSlug, setCustomSlug] = useState(() => slugify(asset.getName()));
  const [zmlSlug, setZmlSlug] = useState('');

  useEffect(() => {
    if (!workspaceId) return;
    zwApiClient.getWorkspace(workspaceId)
      .then(workspace => {
        setZmlSlug(workspace.zmlSlug);
      })
      .catch(() => setZmlSlug(''));
  }, [zwApiClient, workspaceId]);

  const handleTemplateChange = (value: string) => {
    const template = availableTemplates.find(t => t.value === value) ?? availableTemplates[0];
    setSelectedTemplate(template);
    setSizeConfig(template.embedOptions);
  };

  useEffect(() => {
    if (!workspaceId) return;
    zwApiClient.zml.getPublicLink(workspaceId, assetId)
      .then(link => {
        setPublicLink(link);
        const qs = (link.querystring ?? {}) as EmbedQuerystring;
        const template = (qs.experience
          ? availableTemplates.find(t => t.value === qs.experience)
          : undefined) ?? availableTemplates[0];
        setSelectedTemplate(template);
        setSizeConfig(buildSizeOptionsFromQuerystring(qs, template.embedOptions));
        setIsEditing(false);
      })
      .catch(() => {
        setPublicLink(null);
      });
  }, [zwApiClient, workspaceId, assetId, availableTemplates]);

  const handleSizeConfigChange = (id: string, value: string | boolean) => {
    setSizeConfig(prev => prev.map(opt => opt.id === id ? { ...opt, value } : opt));
  };

  const handleSave = async () => {
    if (!workspaceId) return;
    setIsSaving(true);
    try {
      const querystring = buildQuerystringFromForm(selectedTemplate.value, sizeConfig);
      const createParam = { querystring, ...(customSlug.length > 0 ? { folder: customSlug } : {}) };
      const updated = publicLink
        ? await zwApiClient.zml.updatePublicLink(workspaceId, assetId, { querystring })
        : await zwApiClient.zml.createPublicLink(workspaceId, assetId, createParam);
      setPublicLink(updated);
      setSizeConfig(buildSizeOptionsFromQuerystring((updated.querystring ?? {}) as EmbedQuerystring, selectedTemplate.embedOptions));
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save public link:', err);
      onError?.(getPublicLinkErrorMessage(err, 'Failed to save public link.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePublicLink = async () => {
    if (!workspaceId) return;
    setIsSaving(true);
    try {
      const querystring = buildQuerystringFromForm(selectedTemplate.value, sizeConfig);
      const createParam = { querystring, ...(customSlug.length > 0 ? { folder: customSlug } : {}) };
      const created = await zwApiClient.zml.createPublicLink(workspaceId, assetId, createParam);
      setPublicLink(created);
      setSizeConfig(buildSizeOptionsFromQuerystring((created.querystring ?? {}) as EmbedQuerystring, selectedTemplate.embedOptions));
      setIsEditing(true);
      setCustomSlug('');
      postSystemComment(assetId, publicLinkCreatedBody());
      if (onPublicLinkCreated) {
        onPublicLinkCreated();
      }
    } catch (err) {
      console.log(typeof(err));
      console.error('Failed to create public link:', typeof(err), err);
      onError?.(getPublicLinkErrorMessage(err, 'Failed to create public link.'));
    } finally {
      setIsSaving(false);
    }
  };

  const resolvedUrl = useMemo(() => {
    if (!publicLink) return '';
    return selectedTemplate.buildUrl(publicLink.url, publicLink.querystring);
  }, [publicLink, selectedTemplate]);

  const isStale = useMemo(() => {
    if (!publicLink) return false;
    if (publicLink.publishedVersion == null) return false;
    return publicLink.publishedVersion < asset.getVersion();
  }, [publicLink, asset]);

  const handleRepublish = async () => {
    if (!workspaceId || !publicLink) return;
    setIsRepublishing(true);
    try {
      // The bare asset object URL (no querystring) — what the CDN edge caches.
      const assetUrl = publicLink.url.split('?')[0];
      // Snapshot what the edge serves before overwriting, so we can detect the flip.
      const baseline = await headLastModified(assetUrl);
      const updated = await zwApiClient.zml.publishPublicLink(workspaceId, assetId, { version: asset.getVersion() });
      setPublicLink(updated);
      // The published version changed server-side immediately — refresh the versions
      // list now so it flags the correct published version (independent of the CDN wait).
      onPublished?.();
      // Wait for the CDN to serve the new bytes, then refresh the preview. Skip the
      // wait if we couldn't read a baseline (can't detect the change reliably).
      if (baseline) {
        await waitForCdnRefresh(assetUrl, baseline);
      }
      setPreviewBust(Date.now());
    } catch (err) {
      console.error('Failed to re-publish public link:', err);
      onError?.(getPublicLinkErrorMessage(err, 'Failed to re-publish.'));
    } finally {
      setIsRepublishing(false);
    }
  };

  const embedCode = useMemo(() => {
    if (!publicLink) return '';
    return buildEmbedCode(resolvedUrl, sizeConfig);
  }, [publicLink, resolvedUrl, sizeConfig]);

  useEffect(() => {
    if (!publicLink || !onEmbedCodeBuilt) return;
    // The preview re-fetches via a cache-buster after a republish; the copyable
    // Direct link / Embed code shown in the tab stay unbusted.
    const previewCode = previewBust ? buildEmbedCode(bustUrl(resolvedUrl, previewBust), sizeConfig) : embedCode;
    onEmbedCodeBuilt(previewCode);
  }, [publicLink, embedCode, onEmbedCodeBuilt, previewBust, resolvedUrl, sizeConfig]);

  const isValidSlug = (slug: string) => {
    // Regex: Allows alphanumeric and hyphens
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return slugRegex.test(slug);
  };

  const handleSlugChange = (value: string) => {
    if (value === '' || isValidSlug(value)) {
      setCustomSlug(value);
    }
  }

  const getPublicLinkPreviewUrl = () => {
    return (customSlug.length > 0
      ? <>{zmlSlug}/<span>{customSlug}</span></>
      : `${zmlSlug}/${assetId}`);
  }

  if (publicLink === undefined) {
    return (
      <div className="embed-tab">
        <div className="embed-tab--empty">
          <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
        </div>
      </div>
    );
  }

  if (publicLink === null) {
    if (!havePermissionToPublish) {
      return (
        <div className="embed-tab flex column center">
          <p>You do not have permission to publish assets.</p>
        </div>
      );
    }

    return (
      <div className="embed-tab flex column">
        <p>Publish your asset, customize viewer controls, and embed it anywhere.</p>

        <section>
          <div className='field'>
            <div className='label-wrapper'>
              <label>Custom slug (optional)</label>
            </div>
            <div className='input-wrapper'>
              <input type="text" value={customSlug} onChange={e => setCustomSlug(e.target.value)} onBlur={() => handleSlugChange(customSlug)} className={ customSlug.length === 0 || isValidSlug(customSlug) ? '' : 'invalid'}/>
            </div>
          </div>

          <div>
            <h4 className='mb8'>Unique link ID</h4>
            <p className='public-link-preview'>
              {getPublicLinkPreviewUrl()}
            </p>
          </div>

          <button
            className={`btn btn-small btn-primary w100 mt8 ${isSaving ? 'processing' : ''}`}
            onClick={handleCreatePublicLink}
            disabled={isSaving || (customSlug.length > 0 && !isValidSlug(customSlug))}
            aria-label="Create a public link for this asset"
          >
            Create public link
          </button>
        </section>

      </div>
    );
  }

  const publishedDate = publicLink?.createdAt ? new Date(publicLink.createdAt) : null;
  const toggleOptions = sizeConfig.filter(o => o.id !== 'width' && o.id !== 'height');

  return (
    <div className="embed-tab">
      <p>Easily share interactive 3D models on the web.</p>
      {isStale && publicLink && (
        <div className="public-link-stale-notice mt24">
          {havePermissionToPublish && (
            <button
              type="button"
              className={`w100 btn btn-medium btn-primary ${isRepublishing ? 'processing' : ''}`}
              onClick={handleRepublish}
              disabled={isRepublishing}
              aria-label="Re-publish this asset"
            >
              Re-publish with latest version
            </button>
          )}

          <p className="mt8">
            Latest version: V{asset.getVersion()}
            <br />
            Published version: V{publicLink.publishedVersion}
          </p>
        </div>
      )}
      <div className="embed-tab-header asset-info-row">
        <h4>Settings</h4>
        {!isEditing && publicLink && havePermissionToPublish && (
          <button
            className="btn btn-small btn-link"
            onClick={() => setIsEditing(true)}
            aria-label="Edit embed settings"
          >
            Edit
          </button>
        )}
      </div>

      {isEditing && (
        <div className="embed-config-section">
          <section>
            <div className='field'>
              <div className='label-wrapper'>
                <label htmlFor="template-select">Select template</label>
              </div>
              <div className='input-wrapper'>
                <select
                  value={selectedTemplate.value}
                  onChange={e => handleTemplateChange(e.target.value)}
                  name="template-select"
                  aria-label="Select template"
                >
                  {availableTemplates.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section>
            <h4>Size</h4>
            <div className="size-config--size">
              <input
                type="number"
                value={sizeConfig.find(o => o.id === 'width')?.value.toString()}
                onChange={e => handleSizeConfigChange('width', e.target.value)}
              />
              <span>X</span>
              <input
                type="number"
                value={sizeConfig.find(o => o.id === 'height')?.value.toString()}
                onChange={e => handleSizeConfigChange('height', e.target.value)}
              />
            </div>
            {toggleOptions.length > 0 && (
              <ul className="size-config--options">
                {toggleOptions.map(opt => (
                  <li key={opt.id}>
                    <ZprToggle
                      value={String(opt.value)}
                      onChanged={(e: ZprToggleCustomEvent<string>) => handleSizeConfigChange(opt.id, e.detail === 'true')}
                      id={`${opt.id}-toggle`}
                    />
                    {opt.label && <span>{opt.label}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <button
              className="btn btn-small btn-secondary"
              onClick={handleSave}
              disabled={isSaving}
              aria-label="Save"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {publishedDate && <span>Last published: {formatDate(publishedDate)}</span>}
          </section>
        </div>
      )}

      {!isEditing && publicLink && (
        <div className="embed-code-section">
          <section>
            <div className="asset-info-row">
              <p>Template: {selectedTemplate.label}</p>
            </div>
            <div className="asset-info-row">
              <p>Size: {sizeConfig.find(o => o.id === 'width')?.value}x{sizeConfig.find(o => o.id === 'height')?.value}</p>
            </div>
            {publishedDate && (
              <div className="asset-info-row">
                <p>Published: {formatDate(publishedDate)}</p>
              </div>
            )}
            {toggleOptions.length > 0 && (
              <ul className="size-config--options">
                {toggleOptions.map(opt => (
                  <li key={opt.id}>
                    {opt.value ? <SuccessIcon width={16} height={16} /> : <FailIcon width={16} height={16} />} {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <p><b>Unique link ID</b><br />{zmlSlug}/{customSlug || assetId}</p>
            <InlineInput
              name="direct-link"
              label='Direct link'
              readOnly
              value={resolvedUrl}
              copyable
              ariaLabel="Direct link"
            />
            <InlineInput
              name="embed-code"
              label='Embed code'
              readOnly
              value={embedCode}
              copyable
              ariaLabel="Embed code"
              isTextarea
            />
          </section>
        </div>
      )}
    </div>
  );
};
